import { useCallback, useEffect, useMemo, useState } from "react";

import {
  IBaseInfoBlock,
  IContact,
  IInfoBlockAPIOrigin,
  IInfoBlockItem,
  IInfoBlockItemType,
  IPlace,
} from "shared/types";
import {
  IUserPermissions,
  getInfoBlockItemActions,
  useInfoBlockItemsProps,
} from "shared/utils";

interface EditInfoBlockItemsHookProps {
  userPermissions: IUserPermissions;
  infoBlockLabel?: string;
  defaultValues?: Omit<IBaseInfoBlock, "id">;
  setApiOrigin?: (origin: IInfoBlockAPIOrigin) => void;
  unlinkFromInfoBlock?: (
    item: IInfoBlockItemType,
    type: IInfoBlockItem,
    setOrigin?: boolean
  ) => void;
}

export const useEditInfoBlockItems = ({
  infoBlockLabel,
  defaultValues,
  setApiOrigin,
  userPermissions,
  unlinkFromInfoBlock,
}: EditInfoBlockItemsHookProps) => {
  const [selectedItem, setSelectedItem] =
    useState<IInfoBlockItemType | null>(null);
  const [openChooseModal, setOpenChooseModal] =
    useState<IInfoBlockItem | null>(null);
  const [openAddModal, setOpenAddModal] = useState<IInfoBlockItem | null>(null);
  const [openEditModal, setOpenEditModal] =
    useState<IInfoBlockItem | null>(null);
  const [openDeleteModal, setOpenDeleteModal] =
    useState<IInfoBlockItem | null>(null);

  const { contacts, places, files, images } = useMemo(
    () =>
      defaultValues || {
        contacts: undefined,
        places: undefined,
        files: undefined,
        images: undefined,
      },
    [defaultValues]
  );

  const getItemActions = useCallback(
    (id: string, type: IInfoBlockItem) => {
      const getItemFromId = (
        id: string,
        type: IInfoBlockItem
      ): IInfoBlockItemType | null => {
        switch (type) {
          case IInfoBlockItem.contact: {
            return contacts?.find((item) => item.id === id) || null;
          }
          case IInfoBlockItem.place: {
            return places?.find((item) => item.id === id) || null;
          }
          case IInfoBlockItem.file: {
            return files?.find((item) => item.id === id) || null;
          }
          case IInfoBlockItem.image: {
            return images?.find((item) => item.id === id) || null;
          }
          default: {
            return null;
          }
        }
      };

      const unlinkAction =
        (type === IInfoBlockItem.contact || type === IInfoBlockItem.place) &&
        unlinkFromInfoBlock
          ? () => {
              // unlink
              const item = getItemFromId(id, type);
              item && unlinkFromInfoBlock(item, type, true);
            }
          : undefined;

      return getInfoBlockItemActions(
        userPermissions,
        () => {
          // edit
          setOpenEditModal(type);
          setSelectedItem(getItemFromId(id, type));
        },
        () => {
          // delete
          setOpenDeleteModal(type);
          setSelectedItem(getItemFromId(id, type));
        },
        infoBlockLabel,
        unlinkAction
      );
    },
    [
      userPermissions,
      infoBlockLabel,
      contacts,
      places,
      files,
      images,
      unlinkFromInfoBlock,
    ]
  );

  // set API origin to determine where
  // to show loading/error states of the API
  useEffect(() => {
    if (!setApiOrigin) return;

    if (openAddModal) {
      setApiOrigin(IInfoBlockAPIOrigin.CreateModal);
      return;
    }

    if (openChooseModal) {
      setApiOrigin(IInfoBlockAPIOrigin.ChooseModal);
      return;
    }

    if (openEditModal) {
      setApiOrigin(IInfoBlockAPIOrigin.EditModal);
      return;
    }

    if (openDeleteModal) {
      setApiOrigin(IInfoBlockAPIOrigin.DeleteModal);
      return;
    }

    setApiOrigin(IInfoBlockAPIOrigin.Form);
  }, [
    setApiOrigin,
    openChooseModal,
    openAddModal,
    openEditModal,
    openDeleteModal,
  ]);

  const getDisabledItems = useCallback(
    (type: IInfoBlockItem) => {
      switch (type) {
        case IInfoBlockItem.contact:
          return contacts || ([] as IContact[]);
        case IInfoBlockItem.place:
          return places || ([] as IPlace[]);
        default:
          return [] as IInfoBlockItemType[];
      }
    },
    [contacts, places]
  );

  const itemsProps = useInfoBlockItemsProps(
    {
      places,
      contacts,
      files,
      images,
    },
    "edit"
  );

  return {
    selectedItem,
    openAddModal,
    setOpenAddModal,
    openChooseModal,
    setOpenChooseModal,
    openEditModal,
    setOpenEditModal,
    openDeleteModal,
    setOpenDeleteModal,
    getItemActions,
    getDisabledItems,
    itemsProps,
  };
};
